import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Container, Form, Nav, Pagination, Row, Spinner, Tab } from 'react-bootstrap';
import { FaArrowLeft } from 'react-icons/fa';
import Flag from 'react-world-flags';
import Layout from "../../components/Layouts/Layouts/Layout";
import Layout2 from "../../components/Layouts/Layouts/Layout2";
import DashboardHeader from "../../components/basic/DashboardHeader";
import { getURLbyEndPointV2 } from '../../store/api';
import { countryNameToCode } from '../../store/countryCode';

const CollaborationPortal = () => {
  const [activeTab, setActiveTab] = useState('countries');
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const countriesPerPage = 12;

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(getURLbyEndPointV2("getParticipateCountries"));
      const sortedCountries = response.data.data.sort((a, b) =>
        a.country.toLowerCase().localeCompare(b.country.toLowerCase())
      );
      setCountries(sortedCountries);
    } catch (error) {
      console.error("Error fetching countries:", error);
      setError("Failed to load countries. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleCountrySelect = (countryData) => {
    setSelectedCountry(countryData);
    setSelectedCompany(countryData.details[0]);
    setActiveTab('countryDetails');
    setCurrentPage(1);
  };

  const handleDropdownSelect = (countryId) => {
    const countryData = countries.find(c => c._id === countryId);
    if (countryData) {
      handleCountrySelect(countryData);
    }
  };

  const fetchRegistrationDetails = async (country, company) => {
    try {
      const response = await axios.get(`${getURLbyEndPointV2("getRegistrationDetails")}/${company._id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching registration details:", error);
      setError("Failed to load registration details.");
      return null;
    }
  };

  const renderCountriesList = () => {
    const indexOfLastCountry = currentPage * countriesPerPage;
    const indexOfFirstCountry = indexOfLastCountry - countriesPerPage;
    const currentCountries = countries.slice(indexOfFirstCountry, indexOfLastCountry);

    return (
      <>
        <Row className="mb-4">
          <Col md={6}>
            <Form.Select
              onChange={(e) => handleDropdownSelect(e.target.value)}
              defaultValue=""
              disabled={loading}
            >
              <option value="" disabled>Select a country</option>
              {countries.map((country) => (
                <option key={country._id} value={country._id}>
                  {country.country}
                </option>
              ))}
            </Form.Select>
          </Col>
        </Row>
        {error && <div className="text-danger mb-3">{error}</div>}
        <Row className="justify-content-center g-4">
          {loading ? (
            <div className="d-flex justify-content-center">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            currentCountries.map((countryData) => (
              <Col key={countryData._id} xs={12} sm={6} md={4} lg={4}>
                <Card
                  className="h-100 cursor-pointer border-1 shadow-sm"
                  onClick={() => handleCountrySelect(countryData)}
                >
                  <Card.Body className="text-center">
                    <div className="d-flex gap-1 justify-content-center align-items-center">
                      <Flag
                        code={countryNameToCode[countryData.country]}
                        style={{ width: '80px', height: '40px', objectFit: 'cover' }}
                        className='border'
                      />
                    </div>
                    <Card.Title className="fw-semibold mt-2 fs-6 mb-0">
                      {countryData.country}
                    </Card.Title>
                  </Card.Body>
                </Card>
              </Col>
            ))
          )}
        </Row>
        {countries.length > countriesPerPage && (
          <div className="d-flex justify-content-center mt-4">
            <Pagination>
              <Pagination.Prev
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1 || loading}
              />
              {Array.from({ length: Math.ceil(countries.length / countriesPerPage) }).map((_, idx) => (
                <Pagination.Item
                  key={idx}
                  active={currentPage === idx + 1}
                  onClick={() => setCurrentPage(idx + 1)}
                  disabled={loading}
                >
                  {idx + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                onClick={() => setCurrentPage(p => Math.min(Math.ceil(countries.length / countriesPerPage), p + 1))}
                disabled={currentPage === Math.ceil(countries.length / countriesPerPage) || loading}
              />
            </Pagination>
          </div>
        )}
      </>
    );
  };

  const renderCountryDetails = () => {
    if (!selectedCountry || !selectedCompany) return null;

    return (
      <Container className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex gap-2 align-items-center">
            <Flag
              code={countryNameToCode[selectedCountry.country]}
              style={{ width: '100px', height: '50px' }}
            />
            <h5 className="fw-bold mb-0">{selectedCountry.country}</h5>
          </div>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setActiveTab('countries')}
          >
            <FaArrowLeft className="me-2" /> Back to Countries
          </Button>
        </div>
        <hr />


        {selectedCountry.details.length > 1 && (
          <Nav variant="pills" activeKey={selectedCompany?._id} className="mb-4">
            {selectedCountry.details.map((company) => (
              <Nav.Item key={company._id} className="me-2">
                <Nav.Link
                  eventKey={company._id}
                  onClick={() => {
                    setSelectedCompany(company);
                    fetchRegistrationDetails(selectedCountry.country, company);
                  }}
                  style={{
                    backgroundColor: selectedCompany?._id === company._id ? '#007bff' : '#f4f4f9',
                    color: selectedCompany?._id === company._id ? '#fff' : '#495057',
                    borderRadius: '5px',
                    padding: '10px 15px',
                  }}
                >
                  {company.companyName}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        )}

        <div className="mb-4">
          <h3 className="fw-bold">{selectedCompany?.companyName}</h3>
          <hr />
          <h6
            className="text-muted"
            dangerouslySetInnerHTML={{
              __html: (selectedCompany?.heading || '').replace(/\r\n/g, '<br />'),
            }}
          />
          <h5 className="mb-3 mt-4">About</h5>
          <p
            className="text-muted"
            dangerouslySetInnerHTML={{
              __html: (selectedCompany?.description || '').replace(/\r\n/g, '<br />'),
            }}
          />
        </div>
      </Container>
    );
  };

  return (
    <Layout>
      <Layout2>
        <DashboardHeader />
        <div className="my-3">
          <h4>Collaboration Portal</h4>
          <hr />
        </div>

        <div className="dashboardBlock">
          <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
            <Tab.Content>
              <Tab.Pane eventKey="countries">
                {renderCountriesList()}
              </Tab.Pane>
              <Tab.Pane eventKey="countryDetails">
                {renderCountryDetails()}
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </div>
      </Layout2>
    </Layout>
  );
};

export default CollaborationPortal;